package er.prateek.crossly.share

import er.prateek.crossly.config.AppConfig
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.RemoteConfigKeys
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.random.Random

private const val XOR_KEY = "CrosslyShareKey1"
private const val VERSION = "4"
private const val VERSION_V3 = "3"
private const val VERSION_V2 = "2"
private const val VERSION_LEGACY = "1"
private const val ID_LENGTH = 8
private const val ID_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789"

/** Compiled fallback -- also the literal host baked into AndroidManifest.xml's deep-link
 *  intent-filter and the two .well-known files. See [er.prateek.crossly.config.LinksConfig]. */
private const val DEFAULT_HOST = "crossly.erprateek.in"

object ShareLinks {
    /** Read fresh from Remote Config's `links.host` on every access, matching how every other
     *  `app_config` section is read elsewhere (e.g. Settings) -- falls back to [DEFAULT_HOST]
     *  whenever Remote Config hasn't loaded, is malformed, or the field is blank. */
    val HOST: String
        get() = AppConfig.parse(AppContainer.remoteConfig.getString(RemoteConfigKeys.APP_CONFIG))
            .links.host.ifBlank { DEFAULT_HOST }

    val BASE: String get() = "https://$HOST"

    const val APP_SHARE_PATH = "/app-share/"
    const val CHALLENGE_PATH = "/challenge/"
    const val RESULT_PATH = "/result/"
    const val PLAY_PACKAGE = "er.prateek.crossly"

    fun playStoreUrl(token: String, medium: String): String {
        val referrer = "utm_source=crossly&utm_medium=$medium&utm_content=$token&utm_campaign=share"
        return "https://play.google.com/store/apps/details?id=$PLAY_PACKAGE&referrer=${ShareCodec.urlEncode(referrer)}"
    }
}

object ShareCodec {
    fun sanitizeName(raw: String): String =
        raw.trim().replace("|", " ").replace(Regex("\\s+"), " ").take(32)

    fun newShareId(): String = newOpaqueId()

    fun newOpaqueId(): String = buildString(ID_LENGTH) {
        repeat(ID_LENGTH) { append(ID_ALPHABET[Random.nextInt(ID_ALPHABET.length)]) }
    }

    fun encode(payload: SharePayload): String {
        val name = sanitizeName(payload.name)
        val shareId = payload.shareId.ifBlank { newShareId() }
        val origin = compactShareOrigin(payload.origin.ifBlank { currentShareOrigin() })
        val installId = payload.installId.trim()
        val plain = when (payload.kind) {
            SharePayload.Kind.APP_SHARE -> "a|$VERSION|$name|$shareId|$origin|$installId"
            SharePayload.Kind.CHALLENGE ->
                "c|$VERSION|$name|${payload.puzzleId}|$shareId|$origin|${payload.timeMs}|$installId|${payload.score}"
            SharePayload.Kind.RESULT ->
                "r|$VERSION|$name|${payload.puzzleId}|$shareId|$origin|${payload.timeMs}|${payload.opponentTimeMs}|$installId"
        }
        return obfuscate(plain.encodeToByteArray())
    }

    fun decode(token: String): SharePayload? {
        if (token.isBlank()) return null
        val plain = runCatching { deobfuscate(token).decodeToString() }.getOrNull() ?: return null
        val parts = plain.split("|")
        if (parts.size < 3) return null
        val version = parts[1]
        return when (parts[0]) {
            "a" -> decodeAppShare(parts, version)
            "c" -> decodeChallenge(parts, version)
            "r" -> decodeResult(parts, version)
            else -> null
        }
    }

    fun appShareUrl(name: String, installId: String = ""): Pair<String, String> {
        val token = encode(SharePayload(SharePayload.Kind.APP_SHARE, name = name, installId = installId))
        return token to urlFor(token, SharePayload.Kind.APP_SHARE)
    }

    fun challengeUrl(
        name: String,
        puzzleId: String,
        timeMs: Long = 0,
        installId: String = "",
        score: Int = 0,
    ): Pair<String, String> {
        val token = encode(
            SharePayload(
                SharePayload.Kind.CHALLENGE,
                name = name,
                puzzleId = puzzleId,
                timeMs = timeMs,
                installId = installId,
                score = score,
            ),
        )
        return token to urlFor(token, SharePayload.Kind.CHALLENGE)
    }

    fun resultUrl(
        name: String,
        puzzleId: String,
        winnerTimeMs: Long,
        opponentTimeMs: Long,
        installId: String = "",
    ): Pair<String, String> {
        val token = encode(
            SharePayload(
                SharePayload.Kind.RESULT,
                name = name,
                puzzleId = puzzleId,
                timeMs = winnerTimeMs,
                opponentTimeMs = opponentTimeMs,
                installId = installId,
            ),
        )
        return token to urlFor(token, SharePayload.Kind.RESULT)
    }

    fun urlFor(token: String, kind: SharePayload.Kind): String = when (kind) {
        SharePayload.Kind.APP_SHARE -> "${ShareLinks.BASE}${ShareLinks.APP_SHARE_PATH}$token"
        SharePayload.Kind.CHALLENGE -> "${ShareLinks.BASE}${ShareLinks.CHALLENGE_PATH}$token"
        SharePayload.Kind.RESULT -> "${ShareLinks.BASE}${ShareLinks.RESULT_PATH}$token"
    }

    fun parseShareUrl(url: String): Pair<SharePayload, String>? {
        val trimmed = url.trim()
        val token = tokenFromPath(trimmed, ShareLinks.RESULT_PATH)
            ?: tokenFromPath(trimmed, ShareLinks.CHALLENGE_PATH)
            ?: tokenFromPath(trimmed, ShareLinks.APP_SHARE_PATH)
            ?: return null
        val payload = decode(token) ?: return null
        return payload to token
    }

    fun parsePlayReferrer(referrer: String): Pair<SharePayload, String>? {
        val decoded = urlDecode(referrer.trim())
        parseShareUrl(decoded)?.let { return it }
        val params = decoded.split("&", ";").mapNotNull { pair ->
            val idx = pair.indexOf('=')
            if (idx <= 0) null
            else urlDecode(pair.substring(0, idx)) to urlDecode(pair.substring(idx + 1))
        }.toMap()
        val token = params["utm_content"]?.takeIf { it.isNotBlank() } ?: return null
        val payload = decode(token) ?: return null
        return payload to token
    }

    fun isOwnLink(
        payload: SharePayload,
        token: String,
        localInstallId: String,
        outboundTokens: Set<String>,
    ): Boolean {
        if (token in outboundTokens) return true
        val mine = localInstallId.trim()
        val theirs = payload.installId.trim()
        return mine.isNotEmpty() && theirs.isNotEmpty() && mine == theirs
    }

    internal fun tokenFromPlain(plain: String): String = obfuscate(plain.encodeToByteArray())

    private fun decodeAppShare(parts: List<String>, version: String): SharePayload? {
        val name: String
        val shareId: String
        val origin: String
        val installId: String
        when (version) {
            VERSION_LEGACY -> {
                name = parts.drop(2).joinToString("|").trim()
                shareId = ""
                origin = "unknown"
                installId = ""
            }
            VERSION_V2, VERSION_V3 -> {
                if (parts.size < 5) return null
                name = parts[2].trim()
                shareId = parts[3].trim()
                origin = expandShareOrigin(parts[4].trim())
                installId = ""
            }
            VERSION -> {
                if (parts.size < 5) return null
                name = parts[2].trim()
                shareId = parts[3].trim()
                origin = expandShareOrigin(parts[4].trim())
                installId = parts.getOrNull(5).orEmpty().trim()
            }
            else -> return null
        }
        if (name.isEmpty()) return null
        return SharePayload(
            SharePayload.Kind.APP_SHARE,
            name = name,
            shareId = shareId,
            origin = origin,
            installId = installId,
        )
    }

    private fun decodeChallenge(parts: List<String>, version: String): SharePayload? {
        val name: String
        val puzzleId: String
        val shareId: String
        val origin: String
        val timeMs: Long
        val installId: String
        var score = 0
        when (version) {
            VERSION_LEGACY -> {
                if (parts.size < 4) return null
                name = parts[2].trim()
                puzzleId = parts[3].trim()
                shareId = ""
                origin = "unknown"
                timeMs = 0
                installId = ""
            }
            VERSION_V2 -> {
                if (parts.size < 6) return null
                name = parts[2].trim()
                puzzleId = parts[3].trim()
                shareId = parts[4].trim()
                origin = expandShareOrigin(parts[5].trim())
                timeMs = 0
                installId = ""
            }
            VERSION_V3 -> {
                if (parts.size < 6) return null
                name = parts[2].trim()
                puzzleId = parts[3].trim()
                shareId = parts[4].trim()
                origin = expandShareOrigin(parts[5].trim())
                timeMs = parts.getOrNull(6)?.toLongOrNull()?.coerceAtLeast(0) ?: 0
                installId = ""
            }
            VERSION -> {
                if (parts.size < 7) return null
                name = parts[2].trim()
                puzzleId = parts[3].trim()
                shareId = parts[4].trim()
                origin = expandShareOrigin(parts[5].trim())
                timeMs = parts[6].toLongOrNull()?.coerceAtLeast(0) ?: 0
                installId = parts.getOrNull(7).orEmpty().trim()
                score = parts.getOrNull(8)?.toIntOrNull()?.coerceAtLeast(0) ?: 0
            }
            else -> return null
        }
        if (name.isEmpty() || puzzleId.isEmpty()) return null
        return SharePayload(
            SharePayload.Kind.CHALLENGE,
            name = name,
            puzzleId = puzzleId,
            shareId = shareId,
            origin = origin,
            timeMs = timeMs,
            installId = installId,
            score = score,
        )
    }

    private fun decodeResult(parts: List<String>, version: String): SharePayload? {
        if (parts.size < 8) return null
        val name = parts[2].trim()
        val puzzleId = parts[3].trim()
        val shareId = parts[4].trim()
        val origin = expandShareOrigin(parts[5].trim())
        val timeMs = parts[6].toLongOrNull()?.coerceAtLeast(0) ?: return null
        val opponentTimeMs = parts[7].toLongOrNull()?.coerceAtLeast(0) ?: return null
        val installId = when (version) {
            VERSION_V3 -> ""
            VERSION -> parts.getOrNull(8).orEmpty().trim()
            else -> return null
        }
        if (name.isEmpty() || puzzleId.isEmpty()) return null
        return SharePayload(
            SharePayload.Kind.RESULT,
            name = name,
            puzzleId = puzzleId,
            shareId = shareId,
            origin = origin,
            timeMs = timeMs,
            opponentTimeMs = opponentTimeMs,
            installId = installId,
        )
    }

    private fun tokenFromPath(url: String, path: String): String? {
        val idx = url.indexOf(path, ignoreCase = true)
        if (idx < 0) return null
        return url.substring(idx + path.length)
            .substringBefore('?')
            .substringBefore('#')
            .substringBefore('/')
            .trim()
            .takeIf { it.isNotEmpty() }
    }

    @OptIn(ExperimentalEncodingApi::class)
    private fun obfuscate(data: ByteArray): String =
        Base64.UrlSafe.encode(xor(data)).trimEnd('=')

    @OptIn(ExperimentalEncodingApi::class)
    private fun deobfuscate(token: String): ByteArray {
        val padded = when (token.length % 4) {
            0 -> token
            2 -> "$token=="
            3 -> "$token="
            else -> token
        }
        return xor(Base64.UrlSafe.decode(padded))
    }

    private fun xor(data: ByteArray): ByteArray {
        val key = XOR_KEY.encodeToByteArray()
        return ByteArray(data.size) { i -> (data[i].toInt() xor key[i % key.size].toInt()).toByte() }
    }

    internal fun urlDecode(value: String): String {
        val plus = value.replace('+', ' ')
        val out = StringBuilder()
        var i = 0
        while (i < plus.length) {
            val c = plus[i]
            if (c == '%' && i + 2 < plus.length) {
                val hex = plus.substring(i + 1, i + 3)
                val parsed = hex.toIntOrNull(16)
                if (parsed != null) {
                    out.append(parsed.toChar())
                    i += 3
                    continue
                }
            }
            out.append(c)
            i++
        }
        return out.toString()
    }

    fun urlEncode(value: String): String = buildString {
        for (byte in value.encodeToByteArray()) {
            val u = byte.toInt() and 0xFF
            val c = u.toChar()
            if (c.isLetterOrDigit() || c == '-' || c == '_' || c == '.' || c == '~') {
                append(c)
            } else {
                append('%')
                append(u.toString(16).uppercase().padStart(2, '0'))
            }
        }
    }
}

fun puzzleLevelLabel(tierDisplayName: String, indexInTier: Int): String {
    val n = if (indexInTier < 0) 1 else indexInTier + 1
    return "$tierDisplayName Level $n"
}

fun compactShareOrigin(origin: String): String = when (origin.lowercase()) {
    "android", "a" -> "a"
    "ios", "iphone", "ipad", "i" -> "i"
    "desktop", "java", "d" -> "d"
    else -> "w"
}

fun expandShareOrigin(origin: String): String = when (origin.lowercase()) {
    "a", "android" -> "android"
    "i", "ios", "iphone", "ipad" -> "ios"
    "d", "desktop", "java" -> "desktop"
    "w", "web" -> "web"
    else -> origin.ifBlank { "unknown" }
}

fun currentShareOrigin(): String {
    val n = er.prateek.crossly.getPlatform().name.lowercase()
    return when {
        n.startsWith("android") -> "android"
        n.startsWith("ios") || n.startsWith("iphone") || n.startsWith("ipad") -> "ios"
        n.startsWith("java") -> "desktop"
        else -> "web"
    }
}

enum class WebClient {
    ANDROID,
    IOS,
    DESKTOP,
    ;

    val isPhone: Boolean get() = this == ANDROID || this == IOS
}

fun webClientFromUserAgent(userAgent: String): WebClient {
    val ua = userAgent.lowercase()
    return when {
        ua.contains("android") -> WebClient.ANDROID
        ua.contains("iphone") || ua.contains("ipad") || ua.contains("ipod") -> WebClient.IOS
        else -> WebClient.DESKTOP
    }
}
